import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, Circle, Polygon, Marker, Popup, ZoomControl } from 'react-leaflet';
import { Line, Radar } from 'react-chartjs-2';
import { usePageMetadata } from '../hooks/usePageMetadata';
import L from 'leaflet';
import '../styles/gridmaster.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  Filler,
} from 'chart.js';
import { Breadcrumb } from '../components/Breadcrumb';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  Filler
);

// Fix for default Leaflet marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});


// MOCK DATA AND TYPES
interface Voivodeship {
    id: string;
    name: string;
    center: [number, number];
    bounds: [[number, number], [number, number]];
}
interface AnalysisResult {
    id: string;
    voivodeship: string;
    coords: [number, number];
    score: number;
    windSpeed: number;
}
interface Station {
    id: string;
    name: string;
    coords: [number, number];
    voltage: string;
    type: string;
    availableCapacity: number;
}

const voivodeshipsData: Voivodeship[] = [
    {id: 'dolnoslaskie', name: 'Dolnośląskie', center: [51.1, 16.9], bounds: [[50.0, 14.8], [51.8, 17.9]]},
    {id: 'kujawsko-pomorskie', name: 'Kujawsko-Pomorskie', center: [53.0, 18.6], bounds: [[52.3, 17.2], [53.8, 19.7]]},
    {id: 'lubelskie', name: 'Lubelskie', center: [51.2, 22.5], bounds: [[50.0, 21.0], [52.4, 24.1]]},
    {id: 'lubuskie', name: 'Lubuskie', center: [52.1, 15.2], bounds: [[51.3, 14.1], [53.0, 16.4]]},
    {id: 'lodzkie', name: 'Łódzkie', center: [51.8, 19.4], bounds: [[50.8, 18.0], [52.4, 20.8]]},
    {id: 'malopolskie', name: 'Małopolskie', center: [49.9, 20.1], bounds: [[49.0, 18.9], [50.6, 21.5]]},
    {id: 'mazowieckie', name: 'Mazowieckie', center: [52.2, 21.0], bounds: [[51.0, 19.2], [53.5, 23.0]]},
    {id: 'opolskie', name: 'Opolskie', center: [50.6, 17.9], bounds: [[50.0, 17.0], [51.1, 18.6]]},
    {id: 'podkarpackie', name: 'Podkarpackie', center: [50.0, 22.0], bounds: [[49.0, 21.0], [50.8, 23.3]]},
    {id: 'podlaskie', name: 'Podlaskie', center: [53.1, 22.8], bounds: [[52.0, 21.0], [54.4, 23.9]]},
    {id: 'pomorskie', name: 'Pomorskie', center: [54.2, 18.0], bounds: [[53.4, 16.7], [54.8, 19.7]]},
    {id: 'slaskie', name: 'Śląskie', center: [50.3, 19.0], bounds: [[49.4, 18.0], [51.0, 19.8]]},
    {id: 'swietokrzyskie', name: 'Świętokrzyskie', center: [50.8, 20.6], bounds: [[50.1, 19.7], [51.4, 21.8]]},
    {id: 'warminsko-mazurskie', name: 'Warmińsko-Mazurskie', center: [53.8, 20.5], bounds: [[52.6, 19.0], [54.5, 22.9]]},
    {id: 'wielkopolskie', name: 'Wielkopolskie', center: [52.3, 17.0], bounds: [[51.0, 15.5], [53.4, 18.8]]},
    {id: 'zachodniopomorskie', name: 'Zachodniopomorskie', center: [53.5, 15.5], bounds: [[52.3, 14.1], [54.1, 16.7]]}
];

// Helper to create custom station icons
const createStationIcon = (type: 'SE' | 'GPZ') => L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="background: ${type === 'SE' ? '#ff0000' : '#00e676'}; 
                width: ${type === 'SE' ? '16px' : '12px'}; 
                height: ${type === 'SE' ? '16px' : '12px'}; 
                border-radius: 50%; 
                border: 2px solid white;
                box-shadow: 0 0 10px rgba(0,0,0,0.5);">
           </div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10]
});

// Chart configurations
const economicChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { labels: { color: '#e0e0e0' } }, title: { display: true, text: 'Prognoza zwrotu z inwestycji', color: '#888' } },
    scales: { 
        y: { grid: { color: '#333' }, ticks: { color: '#888', callback: (value: any) => value + '%' } },
        x: { grid: { color: '#333' }, ticks: { color: '#888' } }
    }
};

const windRoseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, title: { display: true, text: 'Dominujące kierunki wiatru', color: '#888' } },
    scales: {
        r: {
            grid: { color: '#333' },
            ticks: { color: '#888', backdropColor: 'transparent', max: 30 },
            pointLabels: { color: '#00e676', font: { size: 14, weight: 'bold' as const } }
        }
    }
};

export const GridMasterAiPage: React.FC = () => {
    usePageMetadata({
        title: "GridMaster AI | Analizator Lokalizacji Farm Wiatrowych OZE",
        description: "Profesjonalne narzędzie AI do analizy lokalizacji dla OZE. Optymalizuj inwestycje w farmy wiatrowe dzięki zaawansowanej analityce. Sprawdź demo.",
    });

    const initialEconomicData = {
        labels: ['Rok 1', 'Rok 3', 'Rok 5', 'Rok 7', 'Rok 10', 'Rok 15', 'Rok 20'],
        datasets: [{
            label: 'Skumulowany zwrot [%]',
            data: [-100, -60, -20, 20, 80, 180, 300],
            borderColor: '#00e676',
            backgroundColor: 'rgba(0, 230, 118, 0.1)',
            borderWidth: 3,
            tension: 0.4,
            fill: true
        }]
    };

    const initialWindRoseData = {
        labels: ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'],
        datasets: [{
            label: 'Częstotliwość [%]',
            data: [15, 10, 12, 18, 22, 25, 20, 15],
            backgroundColor: 'rgba(0, 230, 118, 0.3)',
            borderColor: '#00e676',
            borderWidth: 2,
            pointBackgroundColor: '#00e676'
        }]
    };


    // State management
    const mapRef = useRef<L.Map>(null);
    const [activeTab, setActiveTab] = useState('criteria');
    const [sliderValues, setSliderValues] = useState({
        distance: 5, wind: 6.0, settlement: 500, power: 10, area: 50
    });
    const [economicData, setEconomicData] = useState(initialEconomicData);
    const [windRoseData, setWindRoseData] = useState(initialWindRoseData);
    const [layerVisibility, setLayerVisibility] = useState({
        '400kv': true, '220kv': true, '110kv': true, 'substations': true,
        'forests': true, 'wind': true, 'settlements': false,
        'natura': false, 'existing-res': false, 'capacity': false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [analysisProgress, setAnalysisProgress] = useState(0);
    const [analysisStatus, setAnalysisStatus] = useState('Oczekiwanie na rozpoczęcie...');
    const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedVoivodeships, setSelectedVoivodeships] = useState<string[]>([]);
    const [notifications, setNotifications] = useState<string[]>([]);
    
    // MOCK DATA GENERATION - this would be fetched in a real app
    const [powerLines, setPowerLines] = useState<any>({ '400kV': [], '220kV': [], '110kV': [] });
    const [substations, setSubstations] = useState<Station[]>([]);
    const [windData, setWindData] = useState<any[]>([]);
    const [forestsData, setForestsData] = useState<any[]>([]);

    useEffect(() => {
        // Generate mock data on mount
        setPowerLines({
            '400kV': [[[54.3, 18.6], [53.4, 18.5], [52.2, 21.0], [51.1, 21.3], [50.0, 19.9]], [[53.4, 14.5], [52.4, 16.9], [51.8, 19.4], [50.3, 18.9]]],
            '220kV': voivodeshipsData.map(v => [v.center, [v.center[0] + 0.5, v.center[1] + 0.5]]),
            '110kV': voivodeshipsData.map(v => [[v.center[0] - 0.2, v.center[1] - 0.2], [v.center[0] + 0.2, v.center[1] + 0.2]])
        });
        setSubstations(voivodeshipsData.flatMap(v => ([
            { id: `SE_${v.id}`, name: `SE ${v.name}`, coords: v.center, voltage: '400/220/110', type: 'SE', availableCapacity: 150 },
            ...Array.from({ length: 3 }, (_, i) => ({ id: `GPZ_${v.id}_${i}`, name: `GPZ ${v.name} ${i+1}`, coords: [v.center[0] + (Math.random()-0.5)*1.5, v.center[1] + (Math.random()-0.5)*1.5] as [number, number], voltage: '110/15', type: 'GPZ', availableCapacity: 30 }))
        ])));
        setWindData([
            {center: [54.2, 18.0], radius: 80000, avgSpeed: 8.5, name: 'Strefa Pomorska'},
            {center: [53.8, 20.5], radius: 70000, avgSpeed: 7.8, name: 'Strefa Mazurska'},
        ]);
        setForestsData([
            {name: 'Puszcza Białowieska', coords: [[52.7, 23.5], [52.9, 23.5], [52.9, 24.0], [52.7, 24.0]]},
            {name: 'Bory Tucholskie', coords: [[53.5, 17.8], [53.8, 17.8], [53.8, 18.3], [53.5, 18.3]]},
        ]);

        showNotification('🚀 GridMaster Pro gotowy do analizy!');
    }, []);

    // Effect for ROI chart
    useEffect(() => {
        const { distance, wind, power, area } = sliderValues;

        const windFactor = (wind - 3) / (12 - 3); // 0-1
        const distanceFactor = 1 - ((distance - 0.5) / (25 - 0.5)); // 0-1
        const powerFactor = (power - 1) / (50 - 1); // 0-1
        const areaFactor = (area - 10) / (200 - 10); // 0-1

        const overallFactor = 0.5 + 1.5 * (windFactor * 0.5 + distanceFactor * 0.2 + powerFactor * 0.2 + areaFactor * 0.1);

        const baselineROI = [-100, -60, -20, 20, 80, 180, 300];
        const newROIData = baselineROI.map(value => {
            if (value <= 0) {
                return value * (1 + (1 - distanceFactor) * 0.3 - areaFactor * 0.1 - powerFactor * 0.1);
            }
            return value * overallFactor;
        });

        setEconomicData(prev => ({
            ...prev,
            datasets: [{ ...prev.datasets[0], data: newROIData }]
        }));
    }, [sliderValues]);

    // Effect for Wind Rose chart
    useEffect(() => {
        const { wind } = sliderValues;
        const baselineDistribution = [15, 10, 12, 18, 22, 25, 20, 15];
        const factor = 1 + ((wind - 3) / (12 - 3)) * 0.4; // 1.0 to 1.4
        const newDistribution = baselineDistribution.map(val => 
            Math.min(30, Math.round(val * factor * (0.95 + Math.random() * 0.1)))
        );

        setWindRoseData(prev => ({
            ...prev,
            datasets: [{ ...prev.datasets[0], data: newDistribution }]
        }));
    }, [sliderValues.wind]);


    const showNotification = useCallback((message: string) => {
        setNotifications(prev => [...prev, message]);
        setTimeout(() => {
            setNotifications(prev => prev.slice(1));
        }, 5000);
    }, []);

    const runAdvancedAnalysis = () => {
        setIsLoading(true);
        setAnalysisResults([]);
        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            setAnalysisProgress(progress);
            setAnalysisStatus(`Analizowanie danych... ${progress}%`);
            if (progress >= 100) {
                clearInterval(interval);
                performAnalysis();
            }
        }, 200);
    };

    const performAnalysis = () => {
        const targetVoivodeships = selectedVoivodeships.length > 0
            ? voivodeshipsData.filter(v => selectedVoivodeships.includes(v.id))
            : voivodeshipsData;
        
        const results: AnalysisResult[] = [];
        targetVoivodeships.forEach(voiv => {
            for (let i = 0; i < 3; i++) {
                results.push({
                    id: `loc_${voiv.id}_${i}`,
                    voivodeship: voiv.name,
                    coords: [voiv.center[0] + (Math.random() - 0.5) * 1.2, voiv.center[1] + (Math.random() - 0.5) * 1.2],
                    score: Math.random() * 40 + 60,
                    windSpeed: Math.random() * 3 + 5.5
                });
            }
        });
        setAnalysisResults(results);
        setIsLoading(false);
        setAnalysisStatus('Analiza zakończona.');
        showNotification('✅ Analiza zakończona pomyślnie!');
        setActiveTab('analysis');
    };
    
    const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        const key = id.replace('-slider', '') as keyof typeof sliderValues;
        setSliderValues(prev => ({ ...prev, [key]: parseFloat(value) }));
    };

    const getSliderPercentage = (min: number, max: number, value: number) => ((value - min) / (max - min)) * 100;

    const exportResults = () => {
        if (analysisResults.length === 0) {
            showNotification('Brak wyników do eksportu');
            return;
        }
        const geojson = {
            type: 'FeatureCollection',
            features: analysisResults.map(loc => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [loc.coords[1], loc.coords[0]] },
                properties: { id: loc.id, voivodeship: loc.voivodeship, score: loc.score, windSpeed: loc.windSpeed }
            }))
        };
        const dataStr = JSON.stringify(geojson, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', `analiza_oze_${new Date().toISOString().slice(0,10)}.geojson`);
        linkElement.click();
        showNotification('Wyniki wyeksportowane pomyślnie');
    };

    const toggleVoivodeship = (id: string) => {
        setSelectedVoivodeships(prev => 
            prev.includes(id) ? prev.filter(vId => vId !== id) : [...prev, id]
        );
    };
    
    const applyVoivodeshipSelection = () => {
        setIsModalOpen(false);
        if (selectedVoivodeships.length > 0 && mapRef.current) {
            const bounds = L.latLngBounds([]);
            selectedVoivodeships.forEach(id => {
                const voiv = voivodeshipsData.find(v => v.id === id);
                if (voiv) {
                    bounds.extend(voiv.bounds[0]);
                    bounds.extend(voiv.bounds[1]);
                }
            });
            mapRef.current.fitBounds(bounds);
            showNotification(`Wybrano ${selectedVoivodeships.length} województw`);
        }
    };

    const sortedResults = [...analysisResults].sort((a, b) => b.score - a.score).slice(0, 5);

    return (
        <>
            <div className="pt-4 absolute top-[64px] left-0 right-0 z-50">
                <Breadcrumb />
            </div>
            <div className="gridmaster-container">
                {/* Left Sidebar */}
                <div className="sidebar-left">
                    <div className="gridmaster-header">
                        <h1>⚡ GridMaster Pro</h1>
                        <p>Zaawansowana Analiza Lokalizacji OZE</p>
                    </div>
                    
                    <div className="gm-section">
                        <div className="gm-tabs">
                            <button className={`gm-tab ${activeTab === 'criteria' ? 'active' : ''}`} onClick={() => setActiveTab('criteria')}>Kryteria</button>
                            <button className={`gm-tab ${activeTab === 'layers' ? 'active' : ''}`} onClick={() => setActiveTab('layers')}>Warstwy</button>
                            <button className={`gm-tab ${activeTab === 'analysis' ? 'active' : ''}`} onClick={() => setActiveTab('analysis')}>Analiza</button>
                        </div>
                        
                        {activeTab === 'criteria' && (
                            <div id="criteria-panel">
                                <h2>🎯 Parametry Lokalizacji</h2>
                                <div className="criteria-item">
                                    <div className="criteria-header"><span className="criteria-label">Odległość od linii WN/NN</span><span className="criteria-value">{sliderValues.distance} km</span></div>
                                    <input type="range" className="gm-slider" id="distance-slider" min="0.5" max="25" step="0.5" value={sliderValues.distance} onChange={handleSliderChange} style={{'--value': `${getSliderPercentage(0.5, 25, sliderValues.distance)}%`} as React.CSSProperties} />
                                </div>
                                <div className="criteria-item">
                                    <div className="criteria-header"><span className="criteria-label">Min. odległość od zabudowań</span><span className="criteria-value">{sliderValues.settlement} m</span></div>
                                    <input type="range" className="gm-slider" id="settlement-slider" min="200" max="3000" step="50" value={sliderValues.settlement} onChange={handleSliderChange} style={{'--value': `${getSliderPercentage(200, 3000, sliderValues.settlement)}%`} as React.CSSProperties} />
                                </div>
                                <div className="criteria-item">
                                    <div className="criteria-header"><span className="criteria-label">Min. prędkość wiatru</span><span className="criteria-value">{sliderValues.wind.toFixed(1)} m/s</span></div>
                                    <input type="range" className="gm-slider" id="wind-slider" min="3" max="12" step="0.1" value={sliderValues.wind} onChange={handleSliderChange} style={{'--value': `${getSliderPercentage(3, 12, sliderValues.wind)}%`} as React.CSSProperties} />
                                </div>
                                <div className="criteria-item">
                                    <div className="criteria-header"><span className="criteria-label">Min. moc przyłączeniowa</span><span className="criteria-value">{sliderValues.power} MW</span></div>
                                    <input type="range" className="gm-slider" id="power-slider" min="1" max="50" step="1" value={sliderValues.power} onChange={handleSliderChange} style={{'--value': `${getSliderPercentage(1, 50, sliderValues.power)}%`} as React.CSSProperties} />
                                </div>
                                <div className="criteria-item">
                                    <div className="criteria-header"><span className="criteria-label">Min. powierzchnia działki</span><span className="criteria-value">{sliderValues.area} ha</span></div>
                                    <input type="range" className="gm-slider" id="area-slider" min="10" max="200" step="5" value={sliderValues.area} onChange={handleSliderChange} style={{'--value': `${getSliderPercentage(10, 200, sliderValues.area)}%`} as React.CSSProperties} />
                                </div>
                                <button className="gm-button" onClick={runAdvancedAnalysis} disabled={isLoading}>🚀 Uruchom Zaawansowaną Analizę</button>
                                <button className="gm-button secondary" style={{marginTop: '10px'}} onClick={() => setIsModalOpen(true)}>📍 Wybierz Województwa</button>
                            </div>
                        )}
                        
                        {activeTab === 'layers' && (
                            <div id="layers-panel">
                                <h2>🗺️ Warstwy Danych</h2>
                                {Object.keys(layerVisibility).map(key => {
                                    const labels: { [key: string]: any } = { '400kv': { label: 'Linie 400 kV', icon: '⚡', color: '#ff0000' }, '220kv': { label: 'Linie 220 kV', icon: '⚡', color: '#ff7700' }, '110kv': { label: 'Linie 110 kV', icon: '⚡', color: '#ffdd00' }, 'substations': { label: 'Stacje GPZ/SE', icon: '🏭', color: '#00e676' }, 'forests': { label: 'Lasy Państwowe', icon: '🌲', color: '#2e7d32' }, 'wind': { label: 'Mapa wiatrowa', icon: '💨', color: '#00bcd4' } };
                                    if (!labels[key]) return null;
                                    return (
                                        <div key={key} className="layer-toggle">
                                            <div className="layer-info">
                                                <div className="layer-icon" style={{ background: labels[key].color }}>{labels[key].icon}</div>
                                                <span>{labels[key].label}</span>
                                            </div>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={layerVisibility[key as keyof typeof layerVisibility]} onChange={() => setLayerVisibility(p => ({...p, [key]: !p[key as keyof typeof layerVisibility]}))} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {activeTab === 'analysis' && (
                             <div id="analysis-panel">
                                <h2>📊 Wyniki Analizy</h2>
                                <div className="gm-analysis-panel">
                                    <h3 style={{ color: '#00e676', marginBottom: '15px' }}>Postęp analizy</h3>
                                    <div className="gm-progress-bar"><div className="gm-progress-fill" style={{ width: `${analysisProgress}%` }}></div></div>
                                    <p style={{ fontSize: '12px', color: '#888', marginTop: '10px' }}>{analysisStatus}</p>
                                </div>
                                <div className="gm-stats-grid">
                                    <div className="gm-stat-card"><h3>{analysisResults.length}</h3><p>Lokalizacji optymalnych</p></div>
                                    <div className="gm-stat-card"><h3>{(analysisResults.length * 50).toFixed(0)}</h3><p>MW dostępnej mocy</p></div>
                                    <div className="gm-stat-card"><h3>{analysisResults.length > 0 ? (analysisResults.reduce((acc, c) => acc + c.windSpeed, 0) / analysisResults.length).toFixed(1) : '0.0'}</h3><p>m/s śr. prędkość</p></div>
                                    <div className="gm-stat-card"><h3>{analysisResults.length > 0 ? '8.5' : '0'}</h3><p>lat zwrot inwestycji</p></div>
                                </div>
                                <button className="gm-button secondary" style={{marginTop: '15px'}} onClick={exportResults}>📥 Eksportuj Wyniki</button>
                            </div>
                        )}
                    </div>
                    
                    <div className="gm-section">
                        <h2>💨 Analiza Wiatrowa</h2>
                        <div className="chart-container"><Radar data={windRoseData} options={windRoseChartOptions} /></div>
                    </div>
                </div>

                {/* Main Content (Map) */}
                <div className="main-content">
                    <MapContainer ref={mapRef} center={[52.0, 19.0]} zoom={6} zoomControl={false}>
                        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="© OpenStreetMap contributors © CARTO" />
                        <ZoomControl position="bottomleft" />
                        
                        {/* Layers */}
                        {layerVisibility['400kv'] && powerLines['400kV'].map((line: any, i: number) => <Polyline key={`400-${i}`} positions={line} color="#ff0000" weight={4} opacity={0.8} dashArray="10, 5" />)}
                        {layerVisibility['220kv'] && powerLines['220kV'].map((line: any, i: number) => <Polyline key={`220-${i}`} positions={line} color="#ff7700" weight={3} opacity={0.7} />)}
                        {layerVisibility['110kv'] && powerLines['110kV'].map((line: any, i: number) => <Polyline key={`110-${i}`} positions={line} color="#ffdd00" weight={2} opacity={0.6} />)}
                        {layerVisibility.substations && substations.map(s => <Marker key={s.id} position={s.coords} icon={createStationIcon(s.type as any)}><Popup>{`<b>${s.name}</b><br/>Napięcie: ${s.voltage} kV<br/>Dostępna moc: ${s.availableCapacity.toFixed(1)} MW`}</Popup></Marker>)}
                        {layerVisibility.wind && windData.map((z, i) => <Circle key={`wind-${i}`} center={z.center} radius={z.radius} pathOptions={{ color: '#00bcd4', fillColor: '#00bcd4', fillOpacity: 0.2, weight: 2 }} />)}
                        {layerVisibility.forests && forestsData.map((f, i) => <Polygon key={`forest-${i}`} positions={f.coords} pathOptions={{ color: '#2e7d32', fillColor: '#2e7d32', fillOpacity: 0.3, weight: 1 }} />)}
                        {analysisResults.map(loc => <Circle key={loc.id} center={loc.coords} radius={5000} pathOptions={{ color: '#00ff00', fillColor: '#00ff00', fillOpacity: 0.3, weight: 2 }}><Popup>{`<b>Lokalizacja optymalna</b><br/>Województwo: ${loc.voivodeship}<br/>Ocena: ${loc.score.toFixed(1)}/100`}</Popup></Circle>)}
                    </MapContainer>
                    
                    <div className="gm-control-panel">
                        <h3 style={{ color: '#00e676', marginBottom: '15px' }}>🎮 Kontrola Mapy</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                            <button className="gm-button secondary" onClick={() => mapRef.current?.fitBounds([[49.0, 14.1], [54.8, 24.1]])}>🇵🇱 Polska</button>
                            <button className="gm-button secondary" onClick={() => showNotification("Funkcja heatmapy w przygotowaniu.")}>🔥 Heatmapa</button>
                            <button className="gm-button secondary" onClick={() => setAnalysisResults([])}>🗑️ Wyczyść</button>
                        </div>
                    </div>
                    
                    <div className="gm-legend">
                        <h3>Legenda</h3>
                        <div className="legend-item"><div className="legend-color" style={{ background: '#ff0000' }}></div><span>Linie 400 kV</span></div>
                        <div className="legend-item"><div className="legend-color" style={{ background: '#00e676' }}></div><span>Obszary optymalne</span></div>
                    </div>
                </div>

                {/* Right Sidebar */}
                <div className="sidebar-right">
                    <div className="gm-section">
                        <h2>📈 Analiza Ekonomiczna</h2>
                        <div className="chart-container"><Line data={economicData} options={economicChartOptions} /></div>
                    </div>
                    <div className="gm-section">
                        <h2>🏆 Ranking Lokalizacji</h2>
                        <div id="location-ranking">
                            {sortedResults.length > 0 ? sortedResults.map((loc, idx) => (
                                <div key={loc.id} className="criteria-item" style={{cursor: 'pointer'}} onClick={() => mapRef.current?.setView(loc.coords, 12)}>
                                    <div style={{fontWeight: 600, color: '#00e676'}}>#{idx + 1} {loc.voivodeship}</div>
                                    <div style={{fontSize: '12px', color: '#888', marginTop: '5px'}}>Ocena: {loc.score.toFixed(1)}/100</div>
                                </div>
                            )) : <p className="text-sm text-gray-500">Uruchom analizę, aby zobaczyć ranking.</p>}
                        </div>
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <div className="gm-modal">
                    <div className="gm-modal-content">
                        <button className="gm-close-modal" onClick={() => setIsModalOpen(false)}>×</button>
                        <h2 style={{ color: '#00e676', marginBottom: '20px' }}>Wybierz Województwa do Analizy</h2>
                        <div className="voivodeship-selector">
                            {voivodeshipsData.map(v => (
                                <button key={v.id} className={`voivodeship-btn ${selectedVoivodeships.includes(v.id) ? 'active' : ''}`} onClick={() => toggleVoivodeship(v.id)}>
                                    {v.name}
                                </button>
                            ))}
                        </div>
                        <button className="gm-button" onClick={applyVoivodeshipSelection} style={{ marginTop: '20px' }}>Zastosuj Wybór</button>
                    </div>
                </div>
            )}
            
            {isLoading && (<div className="gm-loading-overlay"><div className="gm-loader"></div></div>)}
            
            <div style={{position: 'fixed', top: '84px', right: '20px', zIndex: 10001}}>
                {notifications.map((msg, i) => <div key={i} className="gm-notification">{msg}</div>)}
            </div>
        </>
    );
};